import React, { useState, useEffect, useRef } from 'react';
import { ListGroup } from '@/components/ui';
import type { WorkIdAttachment } from '../../../services/workIdService';
import '../../../features/quantified-tracking/components/taskModals/TaskImageAttachments.css';

interface WorkIdImageAttachmentsProps {
  workIdId: number;
  attachments: WorkIdAttachment[];
  /** When false, hides the "Images (n)" and "Other Attachments" headings (e.g. when used inside a comparison panel). Default true. */
  showHeading?: boolean;
}

const WorkIdImageAttachments: React.FC<WorkIdImageAttachmentsProps> = ({
  workIdId,
  attachments,
  showHeading = true,
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageBlobUrls, setImageBlobUrls] = useState<Record<string, string>>({});
  const preloadedAttachmentsRef = useRef<string>('');

  const isImageAttachment = (att: WorkIdAttachment) =>
    att.attachmentType === 'image' ||
    (att.attachmentName || '').toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/);

  const imageAttachments = attachments.filter(isImageAttachment);
  const otherAttachments = attachments.filter((a) => !isImageAttachment(a));

  useEffect(() => {
    return () => {
      Object.values(imageBlobUrls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [imageBlobUrls]);

  const getDownloadUrl = (attachmentId: number) => {
    const baseUrl = process.env.REACT_APP_API_URL || '';
    const cleanBaseUrl = baseUrl.replace('/api', '') || window.location.origin;
    return `${cleanBaseUrl}/api/work-ids/${workIdId}/attachments/${attachmentId}/download`;
  };

  const preloadImages = async (items: WorkIdAttachment[]) => {
    const blobUrls: Record<string, string> = {};
    for (const att of items) {
      try {
        const url = getDownloadUrl(att.id);
        const response = await fetch(url, { credentials: 'include' });
        if (!response.ok) throw new Error(`Failed to load: ${response.status}`);
        const blob = await response.blob();
        blobUrls[att.id.toString()] = URL.createObjectURL(blob);
      } catch (err) {
        console.error('Failed to preload image:', att.attachmentName, err);
      }
    }
    setImageBlobUrls(blobUrls);
  };

  useEffect(() => {
    if (imageAttachments.length === 0) return;
    const key = imageAttachments.map((a) => `${a.id}-${a.attachmentName}`).join('|');
    if (preloadedAttachmentsRef.current !== key) {
      preloadedAttachmentsRef.current = key;
      preloadImages(imageAttachments);
    }
  }, [workIdId, imageAttachments.length, imageAttachments.map((a) => a.id).join(',')]);

  const handleImageClick = (att: WorkIdAttachment) => {
    const blobUrl = imageBlobUrls[att.id.toString()];
    const fallback = getDownloadUrl(att.id);
    setSelectedImage(blobUrl || fallback);
  };

  if (attachments.length === 0) {
    return (
      <p className="tw-mb-0 tw-text-content-muted">No attachments for this work ID.</p>
    );
  }

  return (
    <div className="task-image-attachments">
      {imageAttachments.length > 0 && (
        <>
          {showHeading && <h6 className="tw-mb-3">Images ({imageAttachments.length})</h6>}
          <div className="task-images-grid">
            {imageAttachments.map((att) => (
              <div
                key={att.id}
                className="task-image-item"
                onClick={() => handleImageClick(att)}
              >
                <img
                  src={imageBlobUrls[att.id.toString()] || getDownloadUrl(att.id)}
                  alt={att.attachmentName}
                  className="task-image-thumbnail"
                />
                <div className="task-image-info">
                  <span className="task-image-filename">{att.attachmentName}</span>
                  {att.attachmentNotes && (
                    <span className="task-image-notes" title={att.attachmentNotes}>
                      {att.attachmentNotes}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {otherAttachments.length > 0 && (
        <div className="tw-mt-3">
          {showHeading && <h6 className="tw-mb-2">Other Attachments</h6>}
          <ListGroup variant="flush">
            {otherAttachments.map((att) => (
              <ListGroup.Item key={att.id} className="tw-flex tw-justify-between tw-items-center tw-px-0">
                <a
                  href={getDownloadUrl(att.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-no-underline"
                >
                  <i className="bi bi-file-earmark tw-mr-2" />
                  {att.attachmentName}
                </a>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}

      {selectedImage && (
        <div className="task-image-modal" onClick={() => setSelectedImage(null)}>
          <div className="task-image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="task-image-modal-close"
              onClick={() => setSelectedImage(null)}
            >
              ×
            </button>
            <img src={selectedImage} alt="Full size" className="task-image-modal-image" />
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkIdImageAttachments;
